import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { storeToRefs } from 'pinia';
import { usePage } from '@inertiajs/vue3';
import { usePointsStore } from '@/store/points';
import { useAppStore } from '@/store/app';
import dayjs from 'dayjs';

export function usePointsSidebar() {
    const pointsStore = usePointsStore();
    const appStore = useAppStore();
    const page = usePage();

    const {
        showPointsHistorySidebar,
        selectedProgramForHistory,
        selectedUserForHistory,
        pointsHistoryData,
        loadingPointsHistory,
        rewards,
        cppData,
        pointsHistoryOffset,
        bestTransferRates,
        loadingBestTransferRates,
    } = storeToRefs(pointsStore);

    const {
        closePointsHistorySidebar,
        fetchPointsHistory,
        openPointsHistorySidebar,
        editProgram,
        loadMorePointsHistory,
        goToPreviousHistoryPage,
        deleteProgram,
        editProgramOptions,
        updateHistoryEntry,
        deleteHistoryEntry,
    } = pointsStore;

    // Local state
    const showPeriodDropdown = ref(false);
    const showMoreMenu = ref(false);
    const selectedPeriod = ref('all_time');
    const showEditHistoryModal = ref(false);
    const editingHistoryEntry = ref(null);
    const editBalance = ref('');
    const editBalanceDate = ref('');
    const showDeleteHistoryModal = ref(false);
    const deletingHistoryEntry = ref(null);
    const showCardBreakdown = ref(true);

    const periods = [
        { label: '6 months', value: '6_months' },
        { label: '12 months', value: '12_months' },
        { label: 'All time', value: 'all_time' },
    ];

    const dateConfig = {
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'M d, Y',
        allowInput: true,
        defaultDate: new Date(),
    };

    // Computed
    const cdnUrl = computed(() => window.cdnUrl);

    const displayPeriod = computed(() => {
        const period = periods.find((p) => p.value === selectedPeriod.value);
        return period ? period.label : 'All time';
    });

    const hasHistoryData = computed(() => {
        return (
            pointsHistoryData.value?.labels?.length > 0 &&
            pointsHistoryData.value?.datasets?.length > 0
        );
    });

    const programCpp = computed(() => {
        if (!selectedProgramForHistory.value || !cppData.value) return null;
        const cpp = cppData.value[selectedProgramForHistory.value.id];
        return cpp?.cpp_cents ? cpp.cpp_cents : null;
    });

    const formattedCpp = computed(() => {
        if (!programCpp.value) return null;
        return parseFloat(programCpp.value).toFixed(1);
    });

    const cppTooltipText = computed(() => {
        if (programCpp.value) {
            return `${parseFloat(programCpp.value).toFixed(1)} cents per point (CPP) - average value My10x users get with ${selectedProgramForHistory.value.name}`;
        }
        return "Average cents per point (CPP) value isn't available for this program";
    });

    const programTypeName = computed(() => {
        if (!selectedProgramForHistory.value?.type) return '';
        const typeMap = {
            airline: 'Airline',
            hotel: 'Hotel',
            rail: 'Rail',
            cashback: 'Cashback',
            flexible_currency: 'Flexible Currency',
        };
        return (
            typeMap[selectedProgramForHistory.value.type] ||
            selectedProgramForHistory.value.type
        );
    });

    const updateType = computed(() => {
        if (!selectedProgramForHistory.value) return 'Unknown';
        if (selectedProgramForHistory.value.connected) {
            const source =
                selectedProgramForHistory.value.program_user?.rewards_source;
            return source === 'manual' ? 'Manual' : 'Connected';
        }
        return 'Manual';
    });

    const isManualAccount = computed(() => {
        if (!selectedProgramForHistory.value) return false;
        return (
            !selectedProgramForHistory.value.connected ||
            (selectedProgramForHistory.value.connected &&
                selectedProgramForHistory.value.program_user?.rewards_source ===
                    'manual')
        );
    });

    const isSyncedAccount = computed(() => {
        if (!selectedProgramForHistory.value) return false;
        return (
            selectedProgramForHistory.value.connected &&
            selectedProgramForHistory.value.program_user?.rewards_source !==
                'manual'
        );
    });

    const canViewAboutProgram = computed(() => {
        return (
            appStore.userSettings?.features?.['transfer-partners-guide'] ===
            true
        );
    });

    const lastUpdated = computed(() => {
        if (!selectedProgramForHistory.value?.last_updated_at) return 'Unknown';
        const date = new Date(
            selectedProgramForHistory.value.last_updated_at
        );
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    });

    const addedDate = computed(() => {
        if (!selectedProgramForHistory.value?.created_at) return 'Unknown';
        const date = new Date(selectedProgramForHistory.value.created_at);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
    });

    const historyList = computed(() => {
        if (!hasHistoryData.value) return [];

        const list = [];
        const labels = pointsHistoryData.value.labels;
        const datasets = pointsHistoryData.value.datasets;
        const records = pointsHistoryData.value.records || [];

        for (let i = 0; i < labels.length; i++) {
            const parts = labels[i].split('/');
            const date = new Date(
                parseInt(parts[2]),
                parseInt(parts[0]) - 1,
                parseInt(parts[1])
            );
            const nextBalance =
                i < labels.length - 1 ? datasets[i + 1] : datasets[i];
            const change = datasets[i] - nextBalance;

            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const formattedDateForInput = `${year}-${month}-${day}`;

            list.push({
                id: records[i]?.id || null,
                date,
                dateFormatted: date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                }),
                monthYear: date.toLocaleDateString('en-US', {
                    month: 'long',
                    year: 'numeric',
                }),
                balance: datasets[i],
                balanceDate:
                    records[i]?.balance_date || formattedDateForInput,
                change: i === labels.length - 1 ? 0 : change,
            });
        }

        return list;
    });

    const groupedHistory = computed(() => {
        const groups = {};
        historyList.value.forEach((item) => {
            if (!groups[item.monthYear]) {
                groups[item.monthYear] = [];
            }
            groups[item.monthYear].push(item);
        });
        return groups;
    });

    const allPrograms = computed(() => {
        if (!rewards.value?.users || !selectedUserForHistory.value) return [];
        const user = rewards.value.users.find(
            (u) => u.id === selectedUserForHistory.value.id
        );
        return user?.programs || [];
    });

    const currentProgramIndex = computed(() => {
        if (
            !selectedProgramForHistory.value ||
            allPrograms.value.length === 0
        )
            return 0;
        const index = allPrograms.value.findIndex(
            (p) => p.id === selectedProgramForHistory.value.id
        );
        return index >= 0 ? index + 1 : 0;
    });

    const totalPrograms = computed(() => allPrograms.value.length);

    const isFirstProgram = computed(() => currentProgramIndex.value <= 1);

    const isLastProgram = computed(
        () => currentProgramIndex.value >= totalPrograms.value
    );

    const totalHistoryRecords = computed(
        () => pointsHistoryData.value?.total || 0
    );

    const hasMoreRecords = computed(
        () => pointsHistoryData.value?.has_more || false
    );

    const remainingRecords = computed(() => {
        const remaining =
            totalHistoryRecords.value - historyList.value.length;
        return Math.min(50, remaining);
    });

    const hasBestTransferRates = computed(
        () => bestTransferRates.value && bestTransferRates.value.length > 0
    );

    const cardBreakdownData = computed(
        () => pointsHistoryData.value?.cards || []
    );

    const shouldShowCardBreakdown = computed(() => {
        if (!selectedProgramForHistory.value) return false;
        if (!selectedProgramForHistory.value.connected) return false;
        if (selectedProgramForHistory.value.consolidate_rewards) return false;
        if (cardBreakdownData.value.length < 2) return false;
        return true;
    });

    const cardBreakdownTotal = computed(() => {
        return cardBreakdownData.value.reduce(
            (sum, card) => sum + (card.balance != null ? card.balance : 0),
            0
        );
    });

    const transferPartnersGuideEnabled = computed(() => {
        return (
            page.props.userData?.features?.['transfer-partners-guide'] === true
        );
    });

    const transferPartnersPageUrl = computed(() => {
        if (!selectedProgramForHistory.value?.slug) return '#';

        return `/resources/transfer-partners-guide/${selectedProgramForHistory.value.slug}`;
    });

    // Methods
    function getProgramImageUrl(image) {
        if (!image) return '';
        if (typeof image === 'object' && image.path && image.file_name) {
            return `${cdnUrl.value}/${image.path}/_small/${image.file_name}`;
        }
        const cleanImage = image.replace(/^programs?_images\//, '');
        return `https://my10x.nyc3.cdn.digitaloceanspaces.com/uploads/program_images/_small/${cleanImage}`;
    }

    function programImgSrc(partner) {
        const cdn = cdnUrl.value || '';
        const val = partner?.program_image;
        if (!val) return null;

        if (typeof val === 'object' && val.path && val.file_name) {
            return `${cdn}/${val.path}/_small/${val.file_name}`;
        }

        if (typeof val === 'string' && val.startsWith('uploads/')) {
            return `${cdn}/${val.replace('program_images/', 'program_images/_small/')}`;
        }

        if (typeof val === 'string' && val.startsWith('program_images/')) {
            return `${cdn}/uploads/${val.replace('program_images/', 'program_images/_small/')}`;
        }

        if (typeof val === 'string') {
            return `${cdn}/uploads/program_images/_small/${val}`;
        }

        try {
            const match = (allPrograms.value || []).find(
                (p) => p.id === partner.program_id
            );
            if (
                match?.program_image?.path &&
                match?.program_image?.file_name
            ) {
                return `${cdn}/${match.program_image.path}/_small/${match.program_image.file_name}`;
            }
            if (match?.image) {
                return `${cdn}/uploads/program_images/_small/${match.image}`;
            }
        } catch (e) {}

        return null;
    }

    function formatProgramType(type) {
        const typeMap = {
            airline: 'Airline',
            hotel: 'Hotel',
            rail: 'Rail',
            cashback: 'Cashback',
            flexible_currency: 'Flexible Currency',
        };
        return typeMap[type] || type;
    }

    function toggleMoreMenu() {
        showMoreMenu.value = !showMoreMenu.value;
    }

    function togglePeriodDropdown() {
        showPeriodDropdown.value = !showPeriodDropdown.value;
    }

    async function handleDeleteProgram() {
        if (!selectedProgramForHistory.value) return;
        showMoreMenu.value = false;
        try {
            await deleteProgram(selectedProgramForHistory.value);
            closePointsHistorySidebar();
        } catch (error) {
            console.error('Error deleting program:', error);
        }
    }

    function handleSettings() {
        if (!selectedProgramForHistory.value || !selectedUserForHistory.value)
            return;
        showMoreMenu.value = false;
        editProgramOptions(
            selectedProgramForHistory.value,
            selectedUserForHistory.value
        );
    }

    function selectPeriod(period) {
        selectedPeriod.value = period;
        showPeriodDropdown.value = false;
        fetchPointsHistory(period);
    }

    function navigatePeriodPrev() {
        if (pointsHistoryOffset.value >= 50) {
            goToPreviousHistoryPage(selectedPeriod.value);
        }
    }

    function navigatePeriodNext() {
        if (hasMoreRecords.value) {
            showMore();
        }
    }

    function showMore() {
        loadMorePointsHistory(selectedPeriod.value);
    }

    function navigateToPreviousProgram() {
        if (isFirstProgram.value) return;
        const prevProgram =
            allPrograms.value[currentProgramIndex.value - 2];
        if (prevProgram) {
            openPointsHistorySidebar(
                prevProgram,
                selectedUserForHistory.value
            );
        }
    }

    function navigateToNextProgram() {
        if (isLastProgram.value) return;
        const nextProgram = allPrograms.value[currentProgramIndex.value];
        if (nextProgram) {
            openPointsHistorySidebar(
                nextProgram,
                selectedUserForHistory.value
            );
        }
    }

    function handleUpdatePoints() {
        if (selectedProgramForHistory.value && selectedUserForHistory.value) {
            editProgram(
                selectedProgramForHistory.value,
                selectedUserForHistory.value
            );
        }
    }

    function openEditHistoryModal(item) {
        editingHistoryEntry.value = item;
        editBalance.value = item.balance.toLocaleString();
        editBalanceDate.value = item.balanceDate || '';
        showEditHistoryModal.value = true;
    }

    function closeEditHistoryModal() {
        showEditHistoryModal.value = false;
        editingHistoryEntry.value = null;
        editBalance.value = '';
        editBalanceDate.value = '';
    }

    async function confirmEditHistoryEntry() {
        if (!editingHistoryEntry.value) return;

        const balance = parseInt(
            editBalance.value.replace(/,/g, ''),
            10
        );
        if (isNaN(balance) || balance < 0) return;

        const success = await updateHistoryEntry(
            editingHistoryEntry.value.id,
            balance,
            editBalanceDate.value
        );

        if (success) {
            closeEditHistoryModal();
        }
    }

    function handleEditBalanceInput(event) {
        const value = event.target.value.replace(/[^\d]/g, '');
        editBalance.value = value
            ? parseInt(value, 10).toLocaleString()
            : '';
    }

    function openDeleteHistoryModal(item) {
        deletingHistoryEntry.value = item;
        showDeleteHistoryModal.value = true;
    }

    function closeDeleteHistoryModal() {
        showDeleteHistoryModal.value = false;
        deletingHistoryEntry.value = null;
    }

    async function confirmDeleteHistoryEntry() {
        if (!deletingHistoryEntry.value) return;

        const success = await deleteHistoryEntry(
            deletingHistoryEntry.value.id
        );

        if (success) {
            closeDeleteHistoryModal();
        }
    }

    return {
        // Store refs
        showPointsHistorySidebar,
        selectedProgramForHistory,
        selectedUserForHistory,
        pointsHistoryData,
        loadingPointsHistory,
        loadingBestTransferRates,
        bestTransferRates,
        pointsHistoryOffset,

        // Local state
        showPeriodDropdown,
        showMoreMenu,
        selectedPeriod,
        periods,
        dateConfig,
        showEditHistoryModal,
        editingHistoryEntry,
        editBalance,
        editBalanceDate,
        showDeleteHistoryModal,
        deletingHistoryEntry,
        showCardBreakdown,

        // Computed
        cdnUrl,
        displayPeriod,
        hasHistoryData,
        programCpp,
        formattedCpp,
        cppTooltipText,
        programTypeName,
        updateType,
        isManualAccount,
        isSyncedAccount,
        canViewAboutProgram,
        lastUpdated,
        addedDate,
        historyList,
        groupedHistory,
        allPrograms,
        currentProgramIndex,
        totalPrograms,
        isFirstProgram,
        isLastProgram,
        totalHistoryRecords,
        hasMoreRecords,
        remainingRecords,
        hasBestTransferRates,
        cardBreakdownData,
        shouldShowCardBreakdown,
        cardBreakdownTotal,
        transferPartnersGuideEnabled,
        transferPartnersPageUrl,

        // Methods
        closePointsHistorySidebar,
        getProgramImageUrl,
        programImgSrc,
        formatProgramType,
        toggleMoreMenu,
        togglePeriodDropdown,
        handleDeleteProgram,
        handleSettings,
        selectPeriod,
        navigatePeriodPrev,
        navigatePeriodNext,
        showMore,
        navigateToPreviousProgram,
        navigateToNextProgram,
        handleUpdatePoints,
        openEditHistoryModal,
        closeEditHistoryModal,
        confirmEditHistoryEntry,
        handleEditBalanceInput,
        openDeleteHistoryModal,
        closeDeleteHistoryModal,
        confirmDeleteHistoryEntry,
    };
}
